import { createSelector } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import type { AppState } from '@/store/store.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

/**
 * Selector for common cash tile data used across multiple components
 * Memoized to avoid unnecessary recalculations
 */
export const selectCashTileCommonData = createSelector(
  [selectCashTile, (_state: AppState, _tileId: string) => _tileId],
  (tile) => {
    if (!tile) {
      return {
        currencyPair: undefined,
        currency1: undefined,
        currency2: undefined,
        notional: undefined,
        notionalCurrencyIndex: undefined,
        notionalCurrency: undefined,
        maturityDate: undefined,
        maturityDateTenor: undefined,
        productType: undefined,
      };
    }

    return {
      currencyPair: tile.properties.currencyPair,
      currency1: tile.properties.currency1,
      currency2: tile.properties.currency2,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      notionalCurrency: isDefined(tile.properties.notionalCurrencyIndex)
        ? tile.properties.notionalCurrencyIndex === 1
          ? tile.properties.currency1
          : tile.properties.currency2
        : undefined,
      maturityDate: tile.properties.maturityDate,
      maturityDateTenor: tile.properties.maturityDateTenor,
      productType: tile.type,
    };
  },
);

/**
 * Currency of the commercial margin amount, derived from the tile's currency pair and the margin
 * currency index (default 2, like the reducers).
 * Unlike `CashEspPrice` / `CashRfsPrice`, this is available in every price status, so components
 * can display the real currency before any price is streamed.
 */
export const selectCashTileMarginAmountCurrency = createSelector([selectCashTile], (tile): string => {
  if (isNotDefined(tile)) {
    return '';
  }

  // same margin container resolution as `cashCommercialMarginCurrencyToggled`
  const marginContainer =
    isDefined(tile.properties.rfs) && tile.properties.rfs.status === 'STREAMED'
      ? tile.properties.rfs
      : tile.type === 'FxSpot' && tile.properties.margin?.isTieringSuccess === true
        ? tile.properties.margin
        : undefined;

  const currencyIndex = marginContainer?.commercialMarginAmountCurrencyIndex ?? 2;

  return (currencyIndex === 1 ? tile.properties.currency1 : tile.properties.currency2) ?? '';
});

/**
 * Selector for trade capture data
 * Memoized to avoid unnecessary recalculations
 */
export const selectCashTileTradeCapture = createSelector([selectCashTile], (tile) => {
  if (!tile) {
    return {
      tradeCapture: undefined,
      isPriceable: false,
      isTradeCaptureChecking: false,
      errors: undefined,
    };
  }

  return {
    tradeCapture: tile.tradeCapture,
    isPriceable: tile.tradeCapture.status === 'CHECKED' && tile.tradeCapture.isReadyToPrice,
    isTradeCaptureChecking: tile.tradeCapture.status === 'CHECKING',
    errors: tile.tradeCapture && 'errors' in tile.tradeCapture ? tile.tradeCapture.errors : undefined,
  };
});

/**
 * Selector for RFS (Request For Stream) data
 * Memoized to avoid unnecessary recalculations
 */
export const selectCashTileRfsData = createSelector([selectCashTile], (tile) => {
  if (!tile) {
    return {
      rfsId: undefined,
      rfsStatus: undefined,
      quote: undefined,
      remainingTime: undefined,
      progress: undefined,
      traderId: undefined,
    };
  }

  const quote = tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote : undefined;

  return {
    rfsId: tile.properties.rfs?.rfsId,
    rfsStatus: tile.properties.rfs?.status,
    quote,
    remainingTime: quote?.rfsRemainingTime,
    progress: isDefined(quote) ? (100 * quote.rfsRemainingTime) / quote.rfsWindow : undefined,
    traderId: quote?.traderId,
  };
});

/**
 * Selector for execution data needed for RFS execution
 * Combines data from multiple sources
 */
export const selectCashTileExecutionData = createSelector(
  [
    selectCashTile,
    (state: AppState, tileId: string) => selectTileClientId(state, tileId),
    (state: AppState) => getSignalRConnectionId(state),
    (state: AppState, tileId: string) => {
      const tile = selectCashTile(state, tileId);
      return tile?.properties.currencyPair
        ? selectCurrencyPairPrecision(state, tile.properties.currencyPair)
        : undefined;
    },
  ],
  (tile, clientId, signalRConnectionId, currencyPairPrecision) => {
    if (!tile) {
      return {
        clientId: undefined,
        signalRConnectionId: undefined,
        currencyPairPrecision: undefined,
        hasAllRequiredData: false,
      };
    }

    return {
      clientId,
      signalRConnectionId,
      currencyPairPrecision,
      hasAllRequiredData: isDefined(clientId) && isDefined(signalRConnectionId) && isDefined(currencyPairPrecision),
    };
  },
);

/**
 * Selector for NDF-specific data
 * Memoized to avoid unnecessary recalculations
 */
export const selectCashTileNdfData = createSelector([selectCashTile], (tile) => {
  if (tile?.type !== 'FxNdf') {
    return {
      isNdf: false,
      fixingSource: undefined,
      fixingDate: undefined,
      isCrossed: undefined,
      xCurrency: undefined,
    };
  }

  return {
    isNdf: true,
    fixingSource: tile.properties.fixingSource,
    fixingDate: tile.properties.fixingDate,
    isCrossed: tile.properties.isCrossed,
    xCurrency: tile.properties.xCurrency,
  };
});

/**
 * Selector for tile error state
 * Memoized to avoid unnecessary recalculations
 */
export const selectCashTileError = createSelector([selectCashTile], (tile) => {
  if (!tile) {
    return {
      hasError: false,
      error: undefined,
    };
  }

  const tradeCaptureError = tile.tradeCapture?.status === 'ERROR' ? tile.tradeCapture.error : undefined;
  const rateError = tile.properties.error;

  return {
    hasError: isDefined(tradeCaptureError) || isDefined(rateError),
    tradeCaptureError,
    rateError,
    error: tradeCaptureError || rateError,
  };
});
